import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getCenters, deleteCenter, toggleCenterStatus } from '../../services/supabaseService';
import CenterTable from './components/CenterTable';
import CenterFormModal from './components/CenterFormModal';
import AssignUsersModal from './components/AssignUsersModal';
import CenterMetrics from './components/CenterMetrics';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const CenterManagement = () => {
  const { userProfile } = useAuth();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inventoryTypeFilter, setInventoryTypeFilter] = useState('all');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);

  useEffect(() => {
    if (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') {
      fetchCenters();
    }
  }, [userProfile]);

  const fetchCenters = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = {
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        inventoryType: inventoryTypeFilter !== 'all' ? inventoryTypeFilter : undefined,
      };
      const data = await getCenters(filters);
      setCenters(data || []);
    } catch (err) {
      console.error('Error fetching centers:', err);
      setError(err?.message || 'Failed to load centers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCenters();
  };

  const handleAddCenter = () => {
    setSelectedCenter(null);
    setIsFormModalOpen(true);
  };

  const handleEditCenter = (center) => {
    setSelectedCenter(center);
    setIsFormModalOpen(true);
  };

  const handleDeleteCenter = async (centerId) => {
    if (!window.confirm('Are you sure you want to delete this center? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteCenter(centerId);
      fetchCenters();
    } catch (err) {
      console.error('Error deleting center:', err);
      alert(err?.message || 'Failed to delete center');
    }
  };

  const handleToggleStatus = async (centerId, currentStatus) => {
    try {
      await toggleCenterStatus(centerId, !currentStatus);
      fetchCenters();
    } catch (err) {
      console.error('Error toggling center status:', err);
      alert(err?.message || 'Failed to update center status');
    }
  };

  const handleAssignUsers = (center) => {
    setSelectedCenter(center);
    setIsAssignModalOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormModalOpen(false);
    setSelectedCenter(null);
    fetchCenters();
  };

  const handleAssignSuccess = () => {
    setIsAssignModalOpen(false);
    setSelectedCenter(null);
    fetchCenters();
  };

  if (userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Icon name="Lock" size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600">Only administrators can access Center Management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Center Management</h1>
          <p className="text-gray-600 mt-1">Manage all centers, metrics, and user assignments</p>
        </div>
        <Button onClick={handleAddCenter} className="flex items-center gap-2">
          <Icon name="Plus" size={20} />
          Add Center
        </Button>
      </div>

      {/* Metrics */}
      <CenterMetrics centers={centers} />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by name, location, or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
              className="w-full"
            />
          </div>
          <div className="w-full md:w-48">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e?.target?.value);
              }}
              className="w-full"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="onboarding">Onboarding</option>
            </Select>
          </div>
          <div className="w-full md:w-56">
            <Select
              value={inventoryTypeFilter}
              onChange={(e) => setInventoryTypeFilter(e?.target?.value)}
              className="w-full"
            >
              <option value="all">All Center Types</option>
              <option value="coworking">Coworking</option>
              <option value="managed_office">Managed Office</option>
              <option value="furnished_office">Furnished Office</option>
              <option value="bareshell">Bareshell</option>
            </Select>
          </div>
          <Button onClick={handleSearch} className="flex items-center gap-2">
            <Icon name="Search" size={20} />
            Search
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <Icon name="AlertCircle" size={20} className="text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Centers Table */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : centers?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Building2" size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Centers Found</h3>
            <p className="text-gray-600 mb-4">Get started by adding your first center.</p>
            <Button onClick={handleAddCenter}>Add Center</Button>
          </div>
        ) : (
          <CenterTable
            centers={centers}
            onEdit={handleEditCenter}
            onDelete={handleDeleteCenter}
            onToggleStatus={handleToggleStatus}
            onAssignUsers={handleAssignUsers}
          />
        )}
      </div>

      {/* Modals */}
      {isFormModalOpen && (
        <CenterFormModal
          center={selectedCenter}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedCenter(null);
          }}
          onSuccess={handleFormSuccess}
          userRole={userProfile?.role}
        />
      )}

      {isAssignModalOpen && selectedCenter && (
        <AssignUsersModal
          center={selectedCenter}
          onClose={() => {
            setIsAssignModalOpen(false);
            setSelectedCenter(null);
          }}
          onSuccess={handleAssignSuccess}
        />
      )}
    </div>
  );
};

export default CenterManagement;